import { Controller, useFormContext } from "react-hook-form";
import { addMonths } from "date-fns/esm";

import FormFieldClaimMapping from "components/FormFields/ClaimMapping";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Datepicker from "ds/components/Datepicker";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import Input from "ds/components/Input";
import ListBoxItem from "ds/components/ListBox/Item";
import Select from "ds/components/Select";
import Textarea from "ds/components/Textarea";
import Typography from "ds/components/Typography";
import { useFormValidations } from "hooks/useFormValidations";
import useTypedContext from "hooks/useTypedContext";
import { ApiKeyType, BillingTier, BillingTierFeature } from "types/generated";
import {
  stringIsRequired,
  validateOptionalDateIsAfterNow,
  validateRegExp,
  validateURL,
} from "utils/formValidators";
import { getDocsUrl } from "utils/getDocsUrl";
import useTierFeature from "views/Account/hooks/useTierFeature";
import { SubscriptionContext } from "views/Account/SubscriptionWrapper";

import CustomListBoxItem from "../../CustomListBoxItem";
import ApiKeyCreateFooter from "../../Footer";
import { CreateApiKeyFields } from "../../types";

const ApiKeyCreateStepDetails = () => {
  const { tier } = useTypedContext(SubscriptionContext);

  const isOidcApiKeyFeatureActive = useTierFeature(BillingTierFeature.ApiKeysOidc);

  const isWarningShown =
    tier === BillingTier.Cloud ||
    tier === BillingTier.V3Cloud ||
    tier === BillingTier.V4Starter ||
    tier === BillingTier.V5Starter;

  const builderForm = useFormContext<CreateApiKeyFields>();
  const {
    register,
    watch,
    formState: { errors },
  } = builderForm;

  const runFormValidations = useFormValidations(builderForm);

  const type = watch("type");

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="API key details"
          description="Name your key and choose its type."
        />

        {isWarningShown && (
          <Box margin="0 0 large 0">
            <Banner variant="warning">
              <Typography tag="p" variant="p-body3" margin="0 0 medium 0">
                API keys are virtual users and are billed like regular users, too. Thus, each API
                key used (exchanged for a token) during any given billing cycle counts against the
                total number of users.
              </Typography>
              <ReadMoreDocsLink
                docsUrl={getDocsUrl("/integrations/api#authenticating-with-the-graphql-api")}
              />
            </Banner>
          </Box>
        )}

        {type === ApiKeyType.Secret && (
          <Box margin="0 0 large 0">
            <Banner variant="info">
              Upon API key creation, a file will be generated with API key secret token details.
              Please save this file somewhere safe as it will never be available again.
            </Banner>
          </Box>
        )}

        <FormField label="Name" error={errors?.name?.message}>
          {({ ariaInputProps }) => (
            <Input
              placeholder="Enter API key name"
              error={!!errors?.name}
              {...register("name", {
                validate: stringIsRequired("Name field is required."),
              })}
              {...ariaInputProps}
            />
          )}
        </FormField>

        <Controller
          name="type"
          render={({ field, fieldState }) => (
            <FormField error={fieldState.error?.message} label="Type" fullWidth>
              {({ ariaInputProps }) => (
                <Box direction="column" gap="x-large">
                  <Select
                    placeholder="Type in or select type from the list"
                    value={field.value}
                    items={[
                      { label: "Secret", value: ApiKeyType.Secret },
                      {
                        label: "OIDC",
                        value: ApiKeyType.Oidc,
                        disabled: !isOidcApiKeyFeatureActive,
                        tooltip: isOidcApiKeyFeatureActive
                          ? undefined
                          : "Upgrade your plan to benefit from ability to use more secure OIDC-based API keys.",
                      },
                    ]}
                    onChange={field.onChange}
                    isError={!!fieldState.error}
                    {...ariaInputProps}
                  >
                    {(itemProps) => (
                      <ListBoxItem
                        id={itemProps.value}
                        key={itemProps.label}
                        textValue={itemProps.label}
                        isDisabled={itemProps.disabled}
                      >
                        <CustomListBoxItem {...itemProps} />
                      </ListBoxItem>
                    )}
                  </Select>
                </Box>
              )}
            </FormField>
          )}
        />

        {type === ApiKeyType.Oidc && (
          <>
            <FormField label="Issuer" error={errors?.oidcIssuer?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="https://"
                  error={!!errors?.oidcIssuer}
                  {...register("oidcIssuer", {
                    required: "Issuer field is required.",
                    validate: validateURL({ https: true }),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>
            <FormField label="Client ID (audience)" error={errors?.oidcClientId?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter Client ID (audience)"
                  error={!!errors?.oidcClientId}
                  {...register("oidcClientId", {
                    required: "Client ID (audience) field is required.",
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>
            <FormField label="Subject expression" error={errors?.oidcSubjectExpression?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter subject expression"
                  error={!!errors?.oidcSubjectExpression}
                  {...register("oidcSubjectExpression", {
                    required: "Subject expression field is required.",
                    validate: validateRegExp(),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>
            <Box margin="large 0 0 0" direction="column" gap="medium">
              <FormFieldClaimMapping />
              <Banner variant="info">
                Currently, only the <strong>groups</strong> Spacelift claim name is supported. Any
                other claim names will be ignored.
              </Banner>
            </Box>
          </>
        )}

        <Controller
          name="expiresAt"
          rules={{
            validate: validateOptionalDateIsAfterNow,
          }}
          render={({ field, fieldState }) => (
            <Box direction="column" gap="medium" margin="large 0 0 0">
              <FormToggleField
                variant="switch"
                title="Expiration"
                checked={!!field.value}
                onChange={(checked) => {
                  field.onChange(checked ? addMonths(new Date(), 1) : undefined);
                }}
              />
              {field.value && (
                <FormField error={fieldState.error?.message} noMargin>
                  <Datepicker
                    date={field.value}
                    onChange={field.onChange}
                    minDate={new Date()}
                    withTime
                    fullWidth
                  />
                </FormField>
              )}
            </Box>
          )}
        />

        <FormField label="Description" isOptional>
          {({ ariaInputProps }) => (
            <Textarea
              placeholder="Enter API key description here..."
              {...register("description")}
              {...ariaInputProps}
            />
          )}
        </FormField>
      </FullScreenModalBody>

      <ApiKeyCreateFooter runFormValidations={runFormValidations} />
    </>
  );
};

export default ApiKeyCreateStepDetails;
